import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '../i18n';
import { SUPPORTED_LOCALES, LOCALE_LABELS, isLocale, type Locale } from '../i18n';
import { stripLocale, withLocale, DEFAULT_LOCALE } from '../i18n';

const FLAGS: Record<Locale, string> = {
  en: 'EN',
  hi: 'हि',
  ta: 'த',
  te: 'తె',
  bn: 'ব',
};

export default function LanguageSwitcher() {
  const { t, locale } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang?: string }>();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const pathLocale = lang && isLocale(lang) ? lang : locale;
  const pathRest = stripLocale(location.pathname);
  const atEn = location.pathname === '/' || !lang;

  const switchTo = (next: Locale) => {
    let target: string;
    if (atEn && next === DEFAULT_LOCALE) {
      target = pathRest;
    } else if (next === DEFAULT_LOCALE) {
      target = pathRest;
    } else {
      target = withLocale(next, pathRest);
    }
    setOpen(false);
    navigate(target);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-xs font-medium text-white/90 hover:text-white px-2 py-1 rounded border border-white/20 hover:border-white/40"
        aria-label={t('language.switch_to')}
        aria-expanded={open}
      >
        <span>{FLAGS[pathLocale]}</span>
        <span className="hidden sm:inline">{LOCALE_LABELS[pathLocale]}</span>
        <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none" stroke="currentColor">
          <path d="M3 4.5L6 7.5L9 4.5" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[160px]">
          {SUPPORTED_LOCALES.map((l) => (
            <button
              key={l}
              onClick={() => switchTo(l)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${
                l === pathLocale ? 'text-parksy-700 font-semibold' : 'text-gray-700'
              }`}
            >
              <span className="flex items-center gap-2">
                <span className="text-xs font-mono bg-gray-100 px-1.5 py-0.5 rounded">{FLAGS[l]}</span>
                <span>{LOCALE_LABELS[l]}</span>
              </span>
              {l === pathLocale && (
                <svg className="w-3 h-3 text-parksy-600" viewBox="0 0 12 12" fill="currentColor">
                  <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="1.5" fill="none" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
